import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { DOM } from 'aurelia-pal';
import he from 'he';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { PayrazrService } from '../../services/payrazr';
import { I18N } from 'aurelia-i18n';
import { TemplateData } from '../../models/template-data';
import { DialogService } from 'aurelia-dialog';
import { LegalTermsDialog } from '../legal-dialog/legal-terms.dialog';
import { DigitalWalletSettings } from '../../models/digital-wallet';
import { MerchantConsumerPortalFeeSettings } from '../../models/merchant-consumer-portal-fee-settings';
import { PaymentPlanSettings } from '../../models/payment-plan-settings';
import { computedFrom } from 'aurelia-binding';

@autoinject
export class Confirmation {
	@bindable({ defaultBindingMode: bindingMode.twoWay }) isSubmitting: boolean = false;
	@bindable title: string;
	@bindable templateData: TemplateData;
	@bindable customTemplateName: string;
	@bindable isCustomTemplate: boolean = false;
	@bindable isAch: boolean = false;
	@bindable isDigitalWallet: boolean = false;
	@bindable nameOnAccount: string;
	@bindable merchantKey: number;
	@bindable submitButtonText: string;
	@bindable acceptButtonText: string;
	@bindable forceShowSignature: boolean = false;
	@bindable forceAcceptanceOfTerms: boolean = false;
	@bindable customerAccountKeys: number[];
	@bindable paymentPlanSettings: PaymentPlanSettings = new PaymentPlanSettings();
	@bindable merchantConsumerPortalFeeSettings: MerchantConsumerPortalFeeSettings = new MerchantConsumerPortalFeeSettings();
	@bindable enforceCaptchaCheckbox: boolean = false;
	// digital wallet specific
	@bindable digitalWalletSettings: DigitalWalletSettings;

	showSignature: boolean = false;
	showAcceptanceOfTerms: boolean = false;
	content: any;
	signature: string;
	hashedContent: any;
	validationErrors: Array<string>;
	digitalWalletValidationSubscription: Subscription;
	linkText: string;
	termsLink: string;
	hasAcceptedTerms: boolean = false;
	captchaClicked: boolean = false;

	constructor(
		private element: Element,
		private payrazrService: PayrazrService,
		private i18n: I18N,
		private eventAggregator: EventAggregator,
		private dialogService: DialogService) {}

	attached() {
		if (!this.submitButtonText) {
			this.submitButtonText = this.i18n.i18next.t('submit', 'Submit');
		}

		if (!this.acceptButtonText) {
			this.acceptButtonText = this.i18n.i18next.t('iAccept', 'I Accept');
		}

		if (this.forceShowSignature) {
			this.showSignature = true;
		} else if (this.isAch) {
			this.showSignature = true;
		} else {
			this.showSignature = false;
		}

		if (this.forceAcceptanceOfTerms) {
			this.showAcceptanceOfTerms = true;
		}

		this.linkText = `<a href="#">${this.i18n.i18next.t('websiteTermsOfUse', 'Website Terms of Use')}</a>`;

		this.termsLink = this.i18n.i18next.t('portalForceAgreeToTermsAndConditions', {
			defaultValue: `I authorize this payment in accordance with the terms above and the ${this.linkText}.`, link: this.linkText
		});

		if (this.isDigitalWallet) {
			// setup digital wallet validation listener
			this.digitalWalletValidationSubscription = this.eventAggregator.subscribe('digitalWalletValidationErrorChange', digitalWalletValidationErrors => {
				this.validationErrors = digitalWalletValidationErrors;
			});
		}

		this.chooseTemplate();
	}

	detached() {
		if (this.digitalWalletValidationSubscription) {
			this.digitalWalletValidationSubscription.dispose();
		}
	}

	chooseTemplate() {
		if (this.templateData) {
			if (this.isCustomTemplate) {
				this.getCustomTemplate(this.customTemplateName, this.templateData);
			} else if (this.isAch) {
				this.getTemplate('check', this.templateData);
			} else if (this.isDigitalWallet) {
				this.getTemplate('digitalWallet', this.templateData);
			}
			else {
				this.getTemplate('card', this.templateData);
			}
		}
	}

	// Get a custom template with a given template name
	getCustomTemplate(templateName: string, templateData: TemplateData) {
		return this.payrazrService.applyTemplateAndReturnHash(templateName, templateData).then((result) => {
			this.content = result.templateHtml;
			this.hashedContent = result.hashedValue;
		})
		.catch(error => {
			console.log(`error getting disclosures: ${JSON.stringify(error)}`);
		});
	}

	// Get the generic card/ach confirmation template
	getTemplate(cardOrCheck: string, templateData: TemplateData) {
		return this.payrazrService.getConfirmHtml(cardOrCheck, templateData).then((result) => {
			this.content = he.decode(result);
		});
	}

	get correctSignature() {
		if (this.isEmptyOrSpaces(this.signature)) {
			return false;
		}
		if (this.signature !== this.nameOnAccount){
			return false;
		}
		return true;
	}

	get nameValidation() {
		if (this.isEmptyOrSpaces(this.signature)){
			return '';
		}
		if (this.signature !== this.nameOnAccount){
			return this.i18n.i18next.t('nameProvidedDoesNotMatch', 'The name you provided does not match the name on your account');
		}
		return '';
	}

	get isForceAcceptTermsValidation() {
		return this.forceAcceptanceOfTerms && !this.hasAcceptedTerms;
	}

	isEmptyOrSpaces(str) {
		let empty: boolean = true;
		if (str && /\S/.test(str)){
			empty = false;
		}
		return empty;
	}

	submit() {
		if (!this.isSubmitting) {
			this.isSubmitting = true;
			this.dispatchCustomEvent('confirmation-submit', { content: this.content, hashedContent: this.hashedContent });
			this.resetCaptcha();
		}
	}

	back() {
		this.content = null;
		this.dispatchCustomEvent('confirmation-back');
	}

	dispatchCustomEvent(eventName: any, eventData?: any) {
		let evt = DOM.createCustomEvent(eventName, { bubbles: true, cancelable: true, detail: eventData });

		this.element.dispatchEvent(evt);
	}

	showLegalModal(content: string) {
		if (content === 'terms') {
			this.dialogService.open({ viewModel: LegalTermsDialog, lock: false });
		}
	}

	isApplePay(): boolean {
		return this.isDigitalWallet && (this.digitalWalletSettings?.isApplePay() ?? false);
	}

	showCaptchaCheckbox() {
		return this.enforceCaptchaCheckbox;
	}

	verifyCaptcha() {
		this.captchaClicked = true;
	}

	handleCaptchaExpired() {
		this.captchaClicked = false;
	}

	handleCaptchaError() {
		this.captchaClicked = false;
	}

	resetCaptcha() {
		this.captchaClicked = false;
	}

	@computedFrom('captchaClicked')
	get isCaptchaValid() {
		return this.enforceCaptchaCheckbox ? this.captchaClicked : true;
	}
}
